import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';

const CustomDatePicker = ({ value, onChange, minDate }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
    const pickerRef = useRef(null);

    const selectedDate = value ? new Date(value) : null;
    const minDateObj = minDate ? new Date(minDate) : new Date();

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setShowPicker(false);
            }
        };

        if (showPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPicker]);

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = monthStart.getDay();

    // Create array of days with empty slots for alignment
    const calendarDays = Array(firstDayOfWeek).fill(null).concat(daysInMonth);

    const handleDateSelect = (day) => {
        if (!day) return;
        
        const dayStart = startOfDay(day);
        const minDateStart = startOfDay(minDateObj);
        
        if (isBefore(dayStart, minDateStart)) return;

        const dateStr = format(day, 'yyyy-MM-dd');
        onChange(dateStr);
        setShowPicker(false);
    };

    const handlePrevMonth = () => {
        setCurrentMonth(subMonths(currentMonth, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(addMonths(currentMonth, 1));
    };

    const formatDisplayValue = () => {
        if (!value) return 'Select date';
        const date = new Date(value);
        return format(date, 'MMM dd, yyyy');
    };

    return (
        <div className='relative' ref={pickerRef}>
            {/* Input Display */}
            <button
                type='button'
                onClick={() => setShowPicker(!showPicker)}
                className='w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 font-medium text-left focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between'
            >
                <span className={value ? 'text-slate-900' : 'text-slate-400'}>
                    {formatDisplayValue()}
                </span>
                <svg className='w-5 h-5 text-slate-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                </svg>
            </button>

            {/* Picker Dropdown */}
            <AnimatePresence>
                {showPicker && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className='absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 z-50 w-full min-w-[320px]'
                    >
                        {/* Month Navigation */}
                        <div className='flex items-center justify-between mb-4'>
                            <button
                                type='button'
                                onClick={handlePrevMonth}
                                className='p-2 rounded-lg hover:bg-slate-100 transition-colors'
                            >
                                <ChevronLeftIcon className='w-5 h-5 text-slate-600' />
                            </button>
                            <h3 className='text-sm font-black text-slate-900'>
                                {format(currentMonth, 'MMMM yyyy')}
                            </h3>
                            <button
                                type='button'
                                onClick={handleNextMonth}
                                className='p-2 rounded-lg hover:bg-slate-100 transition-colors'
                            >
                                <ChevronRightIcon className='w-5 h-5 text-slate-600' />
                            </button>
                        </div>

                        {/* Day Labels */}
                        <div className='grid grid-cols-7 gap-1 mb-2'>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                                <div key={day} className='text-center text-[9px] font-black text-slate-400 uppercase tracking-widest py-1'>
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className='grid grid-cols-7 gap-1 mb-2'>
                            {calendarDays.map((day, index) => {
                                if (!day) {
                                    return <div key={`empty-${index}`} />;
                                }

                                const isSelected = selectedDate && isSameDay(day, selectedDate);
                                const isCurrentMonth = isSameMonth(day, currentMonth);
                                const isTodayDate = isToday(day);
                                const isPast = isBefore(startOfDay(day), startOfDay(minDateObj));

                                return (
                                    <button
                                        key={day.toISOString()}
                                        type='button'
                                        onClick={() => handleDateSelect(day)}
                                        disabled={isPast}
                                        className={`
                                            aspect-square rounded-lg text-sm font-semibold transition-all
                                            ${isSelected ? 'bg-blue-600 text-white shadow-lg' : ''}
                                            ${!isSelected && isTodayDate ? 'bg-blue-50 text-blue-600 border border-blue-200' : ''}
                                            ${!isSelected && !isTodayDate && isCurrentMonth && !isPast ? 'text-slate-900 hover:bg-slate-100' : ''}
                                            ${!isCurrentMonth ? 'text-slate-300' : ''}
                                            ${isPast ? 'text-slate-300 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        {format(day, 'd')}
                                    </button>
                                );
                            })}
                        </div>
                        
                        <div className='mt-3 flex justify-center'>
                             <button
                                type='button'
                                onClick={() => {
                                    const now = new Date();
                                    const dateStr = format(now, 'yyyy-MM-dd');
                                    onChange(dateStr);
                                    setShowPicker(false);
                                }}
                                className='px-4 py-2 text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all w-full'
                            >
                                Today
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CustomDatePicker;
